import { creatorbase, getTiktokUserAnalyticsAudiences } from '@round/api';
import chunk from 'lodash/chunk';
import uniq from 'lodash/uniq';
import { isNumber } from 'utility/utility';
import { showNotification } from 'helpers';
import { backupPostsStatuses } from 'Modules/Plans/Posts/helpers';
import copyComponentRenderToClipboard from 'utility/copyToClipboard/copyComponentRenderToClipboard';
import TikTokAudienceProposalClipboardTable, {
    TikTokAudienceProposalClipboardTableRow,
} from 'Modules/Plans/CopyToClipboard/ProposalTable/TikTokAudienceProposalClipboardTable';
import { getGenderSplit, getTopMarkets } from 'Modules/Plans/CopyToClipboard/ProposalTable/audienceProposal.helpers';
import useClientSelect from 'Modules/Plans/Clients/hooks/useClientSelect';
import {
    useClientContentTags,
    useFetchClientInfluencerAccounts,
    useFetchClientInfluencers,
} from '@round/creatorbase-helpers/api-hooks';
import { useFetchInfluencerLocations } from 'Hooks/queries/useInfluencerLocations';
import useProjectDetails from '../../useProjectDetails';
import { CampaignTableRow } from '../../components/ProjectCampaignsTable/ProjectCampaignsTable';
import { useEnsureProjectDetailsTiktokPosts } from '../posts/useProjectDetailsTikTokPosts';

const ANALYTICS_BATCH_SIZE = 50;

export default function useCopyTiktokAudienceProposalToClipboard() {
    const ensurePosts = useEnsureProjectDetailsTiktokPosts();
    const { project, brand, team } = useProjectDetails();
    const { selectedClientId } = useClientSelect();
    const { tags: contentTags } = useClientContentTags(selectedClientId);

    const getClientInfluencerAccounts = useFetchClientInfluencerAccounts();
    const getClientInfluencers = useFetchClientInfluencers();
    const getLocations = useFetchInfluencerLocations();

    return async (campaign: CampaignTableRow, includeCost: boolean) => {
        if (!team) {
            showNotification('Could not copy', 'error');
            return;
        }

        try {
            const caption = `${brand?.name} - ${project?.name}`;
            const rows = (await ensurePosts(campaign.id)).filter(
                (row) => !backupPostsStatuses.includes(row.status as creatorbase.BackupPostStatus)
            );

            const influencerAccountIds = uniq(
                rows.map((row) => row.influencer_account_id).filter((v): v is string => !!v)
            );

            const clientInfluencerAccounts = await getClientInfluencerAccounts({
                clientId: team.client_id,
                influencerAccountIds,
            });

            const clientInfluencerIds = uniq(clientInfluencerAccounts.map((acc) => acc.client_influencer_id));
            const clientInfluencers = await getClientInfluencers({
                ids: clientInfluencerIds,
                clientId: team.client_id,
            });

            const locationIds = uniq(clientInfluencers.map((ci) => ci.location_id).filter(isNumber));
            const locations = await getLocations(locationIds);

            const analyticsUserIds = uniq(rows.map((row) => row.user?.id).filter(isNumber));
            const audiences = (
                await Promise.all(
                    chunk(analyticsUserIds, ANALYTICS_BATCH_SIZE).map((userIds) =>
                        getTiktokUserAnalyticsAudiences({ user_id: userIds })
                    )
                )
            ).flat();

            const items: TikTokAudienceProposalClipboardTableRow[] = rows.map((row) => {
                const influencerAccount = clientInfluencerAccounts.find(
                    (acc) => acc.influencer_account_id === row.influencer_account_id
                );
                const clientInfluencer = clientInfluencers.find(
                    (ci) => ci.id === influencerAccount?.client_influencer_id
                );
                const location = locations.find((loc) => loc.id === clientInfluencer?.location_id);
                const audience = audiences.find((a) => a.user_id === row.user?.id);

                return {
                    account: row.user?.unique_id ?? '',
                    cost: row.cost,
                    followerCount: row.userStatsCache?.follower_count ?? null,
                    estimatedViews: row.estimated_views ?? row.userStatsCache?.avg_post_views ?? null,
                    location: location?.country_name ?? null,
                    engagementRate: row.userStatsCache?.avg_engagement_rate ?? null,
                    genderSplit: getGenderSplit(audience),
                    topMarkets: getTopMarkets(audience),
                    contentTags: contentTags.filter((tag) => clientInfluencer?.content_tags.includes(tag.id)),
                };
            });

            const success = copyComponentRenderToClipboard(
                {
                    caption,
                    items,
                    brief: campaign.brief,
                    campaignName: campaign.name,
                    currency: campaign.currency,
                    includeCost,
                },
                TikTokAudienceProposalClipboardTable
            );

            showNotification(success ? 'Copied!' : 'Could not copy', success ? 'info' : 'error');
        } catch {
            showNotification('Could not copy', 'error');
        }
    };
}
